import { ReactElement } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import {
  ReportAdditionalInformationField,
} from 'shared/domain/report/types/model/common';
import { useReportPageEstimate } from '../hooks/useReportPageEstimate';
import { ReportTranslationKeys } from 'intl/keys';
import { formatTranslation } from 'intl';
import {
  ReportAdditionalInformationSectionPresentational,
  ReportEstimatedSizeBadgePresentational,
} from './presentational';

const REPORT_ESTIMATE_LABEL_KEY = {
  split: ReportTranslationKeys.estimatedReportSizeBadgeSplit,
  single: ReportTranslationKeys.estimatedReportSizeBadge,
} as const;

export {
  ReportAdditionalContentEmptyInfo,
  ReportAttachmentsSubsection,
  ReportContentListItem,
  ReportFormSectionSubheader,
  ReportMessageAttachmentsStyleRow,
  ReportMessageIncludeInPdfCheckbox,
  ReportSuboptionRow,
} from './presentational';
export { ReportFilterChip } from './ReportFilterChip';
export { ReportParticipantPersonalDataSection } from './ReportParticipantPersonalDataSection';

export {
  REPORT_CONTENT_LIST_ITEM_SX,
  REPORT_CONTENT_LIST_SX,
  REPORT_FORM_FULL_WIDTH_SX,
  REPORT_FORM_GRID_SX,
  REPORT_MODAL_EDGE_BLEED_SX,
  REPORT_RECIPIENT_SECTION_SX,
} from './styles';

export function ReportAdditionalInformationSection({
  showIcon = true,
}: {
  showIcon?: boolean;
} = {}): ReactElement {
  const { values, setValues } = useInputForm();

  const toggle = (key: ReportAdditionalInformationField) => {
    if (
      key === ReportAdditionalInformationField.showActiveLinks &&
      !values.showActiveLinks
    ) {
      setValues(ReportAdditionalInformationField.showActiveLinks, true);
      if (!values.showFileNames) {
        setValues(ReportAdditionalInformationField.showFileNames, true);
      }
      return;
    }

    if (
      key === ReportAdditionalInformationField.showFileNames &&
      values.showFileNames &&
      values.showActiveLinks
    ) {
      return;
    }

    setValues(key, !values[key]);
  };

  return (
    <ReportAdditionalInformationSectionPresentational
      showFileNames={values.showFileNames}
      showFileDescriptions={values.showFileDescriptions}
      showActiveLinks={values.showActiveLinks}
      onToggle={toggle}
      showIcon={showIcon}
    />
  );
}

export function ReportEstimatedSizeBadge(): ReactElement | null {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const intl = useIntl();
  const { pages, pageRange, fileCount, isLoading } = useReportPageEstimate();
  const isSplit = fileCount > 1;

  if (isMobile) {
    return null;
  }

  const label = isLoading
    ? '…'
    : formatTranslation(
        intl,
        REPORT_ESTIMATE_LABEL_KEY[isSplit ? 'split' : 'single'],
        { pageRange, pageCount: pages, fileCount }
      );

  return (
    <ReportEstimatedSizeBadgePresentational label={label} isSplit={isSplit} />
  );
}
